// Blog View - Display individual blog post
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useIsAuthenticated } from '@azure/msal-react';
import { marked } from 'marked';
import '../../App.css';
import './BlogView.css';
import Footer from '../Footer';

// Helper function to detect if content is HTML or Markdown (pure, no hooks)
function renderContent(content) {
  const isHTML = /<\/?[a-z][\s\S]*>/i.test(content);
  if (isHTML) {
    return content;
  } else {
    marked.setOptions({
      breaks: true,
      gfm: true,
      headerIds: true,
      mangle: false,
    });
    return marked.parse(content);
  }
}

export default function BlogView() {
  const { id } = useParams();
  const history = useHistory();
  const isAuthenticated = useIsAuthenticated();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [avatarTs] = useState(Date.now());

  useEffect(() => {
    fetchBlog();
  }, [id]);

  // Fetch author profile once the blog (and its authorId) is available
  useEffect(() => {
    if (!blog?.authorId) return;
    fetch(`/api/profile-data?userId=${blog.authorId}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data) setProfile({
          name: `${data.firstName || ''} ${data.secondName || ''}`.trim() || null,
          tagline: null
        });
      })
      .catch(() => {});
  }, [blog?.authorId]);

  // Add copy buttons to code blocks after content renders.
  // The button is attached to a wrapper div (not inside the scrolling <pre>)
  // so it stays pinned top-right even when long scripts scroll horizontally.
  useEffect(() => {
    if (!blog) return;

    const codeBlocks = document.querySelectorAll('.blog-content pre');
    
    codeBlocks.forEach((pre) => {
      // Skip if already wrapped
      if (pre.parentElement?.classList.contains('code-block-wrapper')) return;

      // Wrap the pre in a container so the copy button can be positioned
      // relative to the visible block rather than the scrolling content.
      const wrapper = document.createElement('div');
      wrapper.className = 'code-block-wrapper';
      pre.parentNode.insertBefore(wrapper, pre);
      wrapper.appendChild(pre);

      // Don't add copy button to note/warning callouts
      const codeEl = pre.querySelector('code');
      if (codeEl?.classList.contains('language-note') || codeEl?.classList.contains('language-warning')) return;

      // Add a header bar above the <pre> so the button never overlaps scrolling code
      const header = document.createElement('div');
      header.className = 'code-block-header';

      const button = document.createElement('button');
      button.className = 'code-copy-btn';
      button.innerHTML = '<i class="fas fa-copy"></i>';
      button.title = 'Copy code';

      button.addEventListener('click', async () => {
        const code = codeEl ? codeEl.textContent : pre.textContent;

        try {
          await navigator.clipboard.writeText(code);
          button.innerHTML = '<i class="fas fa-check"></i>';
          button.classList.add('copied');

          setTimeout(() => {
            button.innerHTML = '<i class="fas fa-copy"></i>';
            button.classList.remove('copied');
          }, 2000);
        } catch (err) {
          console.error('Failed to copy code:', err);
        }
      });

      header.appendChild(button);
      wrapper.insertBefore(header, pre);
    });
  }, [blog]);

  const renderedHTML = useMemo(() => {
    if (!blog) return '';
    return renderContent(blog.content);
  }, [blog]);

  const tocItems = useMemo(() => {
    if (!renderedHTML) return [];
    const parser = new DOMParser();
    const doc = parser.parseFromString(renderedHTML, 'text/html');
    const headings = Array.from(doc.querySelectorAll('h1, h2, h3'));
    return headings.map((h, i) => ({
      id: `heading-${i}`,
      text: h.textContent.trim(),
      level: parseInt(h.tagName[1])
    }));
  }, [renderedHTML]);

  const htmlWithIds = useMemo(() => {
    if (!renderedHTML) return '';
    let counter = 0;
    return renderedHTML.replace(/<(h[123])([\s>])/gi, (match, tag, rest) => {
      const id = `heading-${counter++}`;
      return `<${tag} id="${id}"${rest}`;
    });
  }, [renderedHTML]);

  const [activeId, setActiveId] = useState('');

  useEffect(() => {
    if (tocItems.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) setActiveId(entry.target.id);
        });
      },
      { rootMargin: '-80px 0px -60% 0px' }
    );
    tocItems.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [tocItems]);

  const fetchBlog = async () => {
    try {
      setLoading(true);
      // TODO: Replace with actual Azure Blob Storage API call
      const response = await fetch(`/api/blogs/${id}`);
      if (response.ok) {
        const data = await response.json();
        setBlog(data);
      }
    } catch (error) {
      console.error('Error fetching blog:', error);
      // Mock data for development
      setBlog({
        id: '1',
        title: 'Getting Started with Azure',
        content: `# Getting Started with Azure

Azure is Microsoft's cloud computing platform that provides a wide range of services...

## Key Features

- **Scalability**: Scale up or down as needed
- **Security**: Enterprise-grade security
- **Global Reach**: Data centers worldwide

## Example Code

\`\`\`javascript
const azureStorage = require('azure-storage');
const blobService = azureStorage.createBlobService();
\`\`\`

## Images

You can add images to illustrate your points.

## Conclusion

Azure provides powerful cloud solutions for modern applications.`,
        category: 'azure',
        tags: ['azure', 'cloud', 'beginner'],
        createdAt: '2026-03-15T10:00:00Z',
        author: 'Admin'
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="blog-view-container">
        <div className="loading-state">
          <i className="fas fa-spinner fa-spin"></i>
          <p>Loading article...</p>
        </div>
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="blog-view-container">
        <div className="error-state">
          <i className="fas fa-exclamation-circle"></i>
          <h2>Article Not Found</h2>
          <button onClick={() => history.goBack()} className="btn-back">
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const categoryColor = blog.category === 'azure' ? '#0078D4' : blog.category === 'ai' ? '#FF6B6B' : '#7FBA00';

  return (
    <>
      <div className="blog-view-container">
        <div className="blog-view-header">
          <button onClick={() => history.push(`/content/${blog.category}`)} className="btn-back-inline">
            <i className="fas fa-arrow-left"></i> Back to {blog.category.toUpperCase()}
          </button>
          {isAuthenticated && (
            <button onClick={() => history.push(`/blog/edit/${blog.id}`)} className="btn-edit">
              <i className="fas fa-edit"></i> Edit Post
            </button>
          )}
        </div>

        <div className="blog-view-body">
          <article className="blog-article">
            <div className="blog-meta">
              <span className="blog-category-badge" style={{ backgroundColor: categoryColor }}>
                {blog.category.toUpperCase()}
              </span>
              <span className="blog-date">
                <i className="far fa-calendar"></i> {new Date(blog.createdAt).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </span>
              <span className="blog-author-info">
                {blog.author}
                {blog.authorId
                  ? <img
                      src={`/api/profile-avatar?userId=${blog.authorId}&t=${avatarTs}`}
                      alt={blog.author}
                      className="blog-author-avatar"
                      onError={e => { e.target.style.display = 'none'; }}
                    />
                  : <i className="fas fa-user"></i>
                }
              </span>
            </div>

            <h1 className="blog-title">{blog.title}</h1>

            {blog.thumbnail && (
              <div className="blog-thumbnail-wrapper">
                <img src={blog.thumbnail} alt="" className="blog-thumbnail" />
              </div>
            )}

            <div className="blog-tags-section">
              {blog.tags.map(tag => (
                <span key={tag} className="blog-tag-item">#{tag}</span>
              ))}
            </div>

            <div 
              className="blog-content" 
              dangerouslySetInnerHTML={{ __html: htmlWithIds }}
            />

            <div className="blog-footer-actions">
              <button onClick={() => history.push(`/content/${blog.category}`)} className="btn-back-to-list">
                <i className="fas fa-list"></i> View All {blog.category.toUpperCase()} Articles
              </button>
            </div>
          </article>

          {tocItems.length > 0 && (
            <aside className="blog-toc">
              {profile && (
                <div className="blog-author-card">
                  <div className="blog-author-card-avatar">
                    <img
                      src={`/api/profile-avatar?userId=${blog.authorId}&t=${avatarTs}`}
                      alt={profile.name}
                      onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                    />
                    <div className="blog-author-card-avatar-fallback" style={{ display: 'none' }}>
                      <i className="fas fa-user-circle"></i>
                    </div>
                  </div>
                  <div className="blog-author-card-name">{profile.name}</div>
                  <div className="blog-author-card-tagline">{profile.tagline}</div>
                </div>
              )}
              <div className="blog-toc-inner">
                <p className="blog-toc-title">TABLE OF CONTENTS</p>
                <ul className="blog-toc-list">
                  {tocItems.map(item => (
                    <li key={item.id} className={`blog-toc-item toc-level-${item.level} ${activeId === item.id ? 'toc-active' : ''}`}>
                      <a
                        href={`#${item.id}`}
                        onClick={e => {
                          e.preventDefault();
                          const el = document.getElementById(item.id);
                          if (!el) return;
                          const navbarHeight = 80 + 16; // navbar 80px + breathing room
                          const top = el.getBoundingClientRect().top + window.scrollY - navbarHeight;
                          window.scrollTo({ top, behavior: 'smooth' });
                        }}
                      >
                        {item.text}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
}
